/* @jsx h */
import { defineComponent, h, VNode, type CSSProperties } from "vue";
import { cn } from "../utils";

export interface SegmentOption<T> {
  value: T;
  label: string | VNode;
  selectedClass?: string;
  selectedColor?: string;
}

interface SegmentSelectProps<T = any> {
  options: SegmentOption<T>[];
  value: T | T[];
  onChange: (value?: T | T[]) => void;
  className?: string;
  suffix?: () => VNode | null | undefined;
  allowMultiple?: boolean;
  size?: "xs" | "sm" | "md";
}

export const SegmentSelect = defineComponent<SegmentSelectProps>({
  name: "SegmentSelect",
  props: [
    "options",
    "value",
    "onChange",
    "className",
    "suffix",
    "allowMultiple",
    "size",
  ],
  setup(props) {
    const isSelected = (optionValue: any) => {
      if (props.allowMultiple && Array.isArray(props.value)) {
        return props.value.includes(optionValue);
      }
      return props.value === optionValue;
    };

    const handleClick = (optionValue: any) => {
      if (props.allowMultiple) {
        const currentValues = Array.isArray(props.value) ? props.value : [];
        if (currentValues.includes(optionValue)) {
          // Remove from selection
          const newValues = currentValues.filter((v) => v !== optionValue);
          props.onChange(newValues.length > 0 ? newValues : undefined);
        } else {
          // Add to selection
          props.onChange([...currentValues, optionValue]);
        }
      } else {
        props.onChange(props.value === optionValue ? undefined : optionValue);
      }
    };

    return () => (
      <div
        style="box-sizing: border-box;"
        class={cn(" join  overflow-hidden", props.className)}
      >
        {(() => {
          const multipleValues =
            props.allowMultiple && Array.isArray(props.value)
              ? props.value
              : [];
          const hasAllSelected = multipleValues.includes("all");

          const isActive = (optionValue: any) => {
            if (isSelected(optionValue)) return true;
            return hasAllSelected && optionValue !== "all";
          };

          return props.options.map((option, i) => (
            <button
              key={i}
              style={
                isActive(option.value) && option.selectedColor
                  ? ({
                      background: option.selectedColor,
                      borderColor: option.selectedColor,
                      color: "#ffffff",
                    } as CSSProperties)
                  : undefined
              }
              class={cn(
                "btn join-item overflow-hidden transition-colors",
                props.size === "xs" && "btn-xs",
                props.size === "sm" && "btn-sm",
                isActive(option.value)
                  ? (option.selectedClass ??
                      (option.selectedColor
                        ? "shadow-md border-transparent text-white"
                        : "btn-primary"))
                  : "btn-neutral",
              )}
              onClick={() => handleClick(option.value)}
            >
              {option.label}
            </button>
          ));
        })()}
        {props.suffix ? props.suffix() : null}
      </div>
    );
  },
});
